import { Box, useChakraContext } from '@chakra-ui/react'
import {
  Background,
  Connection,
  Controls,
  FinalConnectionState,
  IsValidConnection,
  OnConnect,
  OnConnectEnd,
  OnConnectStart,
  OnReconnect,
  ReactFlow,
  ReactFlowProvider,
  reconnectEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useAtomValue } from 'jotai'
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'

import type { SwitchboardApi } from '../../api/switchboardApi'
import {
  extensionMapAtom,
  extensionsListAtom,
  messagingGroupsAtom,
  messagingGroupsLoadedAtom,
  phoneNumbersAtom,
  schedulesAtom,
  soundClipsAtom,
} from '../../atoms'
import {
  EDGE_INTERACTION_WIDTH,
  EDGE_STROKE_COLOR_HOVER_TOKEN,
  EDGE_STROKE_COLOR_TOKEN,
  EDGE_STROKE_WIDTH,
  EDGE_STROKE_WIDTH_HIGHLIGHTED,
} from '../../constants/styling'
import { getDefaultNodeData } from '../../converters/shared/constants/index'
import type {
  NodeType,
  SwitchboardEdge,
  SwitchboardFlowData,
  SwitchboardNode,
} from '../../converters/shared/reactFlowTypes'
import '../../styles/react-flow.css'
import {
  SwitchboardI18nContext,
  type SwitchboardTranslateFn,
} from '../../types'
import { validateConnection } from '../../utils/connectionValidation'
import type { DayNightSettings } from '../../utils/dayNightSettings'
import { getContentDataSignature } from '../../utils/switchboardFlowSignature'
import { text } from '../../utils/text'
import { DeleteEdgeConfirmationDialog } from '../core/DeleteEdgeConfirmationDialog'
import { DeleteNodeConfirmationDialog } from '../core/DeleteNodeConfirmationDialog'
import { getNodeLabel } from '../NodePalette'
import {
  DAY_NIGHT_HANDLE_TYPES,
  QUEUE_HANDLE_TYPES,
  SCHEDULE_HANDLE_TYPES,
  TRANSFER_AND_ROLLOVER_HANDLE_TYPES,
} from './constants'
import { cleanEdges, createEdgeKey, isCustomHandleType } from './edgeUtils'
import { getNodeTypes } from './nodeTypes'
import { getStructureSignature } from './structureUtils'

export interface SwitchboardCanvasProps {
  api: SwitchboardApi
  initialData?: SwitchboardFlowData
  /**
   * Increment to force a full hydration from initialData, even when structure
   * (node/edge IDs + positions) is unchanged.
   */
  hydrateToken?: number
  onDataChange?: (data: SwitchboardFlowData) => void
  /**
   * Optional registration hook that exposes a flush function to the parent.
   * Calling the registered function immediately emits the latest `{ nodes, edges }`
   * and clears any pending debounced onDataChange timeout.
   */
  registerFlushPendingDataChange?: (flush: () => void) => void
  readOnly?: boolean
  pendingNodeType?: NodeType | null
  onNodeAdded?: () => void
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Day/night settings from PBX (for Day/Night nodes) */
  dayNightSettings?: DayNightSettings | null
  /** PBX ID (for API updates) */
  pbxId?: number
  // Note: Reference data (extensionsList, messagingGroups, etc.) is provided via Jotai atoms
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
  /** Color mode for React Flow theming ('light' | 'dark'). Defaults to 'light'. */
  colorMode?: 'light' | 'dark'
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

// Keep save-flow synchronization tied to this debounce interval.
export const ON_DATA_CHANGE_DEBOUNCE_MS = 50

/**
 * Context for day/night settings and PBX ID
 * Used by Day/Night nodes to access PBX-level configuration
 */
interface DayNightSettingsContextValue {
  api: SwitchboardApi | null
  settings: DayNightSettings | null
  pbxId: number | null
  toast?: ToastLike
}

interface InternalConnectionHandle {
  nodeId: string
  id: string | null
  type: string
}

interface ReactFlowInternalConnectionState {
  fromHandle?: InternalConnectionHandle | null
  inProgress?: boolean
}

interface ReactFlowInternalStore {
  connection?: ReactFlowInternalConnectionState
  connectionClickStartHandle?: InternalConnectionHandle | null
  cancelConnection?: () => void
}

const DayNightSettingsContext =
  React.createContext<DayNightSettingsContextValue>({
    api: null,
    settings: null,
    pbxId: null,
    toast: undefined,
  })

/**
 * Hook to access day/night settings from context
 * @returns Day/night settings and PBX ID, or null values if not available
 */
export const useDayNightSettings = (): DayNightSettingsContextValue => {
  return React.useContext(DayNightSettingsContext)
}

const CanvasInner: React.FC<SwitchboardCanvasProps> = ({
  api,
  initialData,
  hydrateToken,
  onDataChange,
  registerFlushPendingDataChange,
  readOnly = false,
  pendingNodeType,
  onNodeAdded,
  toast,
  dayNightSettings,
  pbxId,
  colorMode = 'light',
  t,
}) => {
  const { screenToFlowPosition } = useReactFlow()
  const reactFlowStore = useStoreApi()

  // Read from Jotai atoms (props are kept in interface for documentation but ignored)
  // Consuming apps should use useSwitchboardDataAtoms to set atoms
  const extensionsList = useAtomValue(extensionsListAtom)
  const extensionMap = useAtomValue(extensionMapAtom)
  const messagingGroups = useAtomValue(messagingGroupsAtom)
  const messagingGroupsLoaded = useAtomValue(messagingGroupsLoadedAtom)
  const soundClips = useAtomValue(soundClipsAtom)
  const schedules = useAtomValue(schedulesAtom)
  const phoneNumbers = useAtomValue(phoneNumbersAtom)
  const initialNodes = useMemo(() => {
    const nodes = (initialData?.nodes ?? []) as SwitchboardNode[]

    return nodes
  }, [initialData])
  const initialEdges = useMemo(
    () => (initialData?.edges ?? []) as SwitchboardEdge[],
    [initialData],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  const [highlightedEdgeId, setHighlightedEdgeId] = useState<string | null>(
    null,
  )
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null)
  const edgeToReconnectRef = useRef<string | null>(null)
  const isDraggingFromConnectedTargetRef = useRef(false)
  const [isDraggingConnection, setIsDraggingConnection] = useState(false)
  const isDraggingConnectionStateRef = useRef(false)
  const isDraggingRef = useRef(false)
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const nodesRef = useRef(nodes)
  const edgesRef = useRef(edges)
  // Keep refs aligned with the current render so save-time flushes read
  // the latest committed graph state without waiting for an effect cycle.
  nodesRef.current = nodes
  edgesRef.current = edges
  useEffect(() => {
    isDraggingConnectionStateRef.current = isDraggingConnection
  }, [isDraggingConnection])

  const [nodeToDelete, setNodeToDelete] = useState<{
    id: string
    label?: string
  } | null>(null)
  const [edgeToDelete, setEdgeToDelete] = useState<string | null>(null)
  const isSyncingRef = React.useRef(false)
  const lastStructureRef = React.useRef<string>('')
  const lastContentDataSignatureRef = React.useRef<string>('')
  const lastHydrateTokenRef = useRef<number | undefined>(undefined)
  const lastValidationErrorsRef = useRef<string>('')

  // Sync from props when structure or persisted node data changes.
  useEffect(() => {
    if (initialData) {
      const newNodes = (initialData.nodes ?? []) as SwitchboardNode[]
      const newEdges = (initialData.edges ?? []) as SwitchboardEdge[]
      const newStructure = getStructureSignature(newNodes, newEdges)
      const newContentDataSignature = getContentDataSignature(
        { nodes: newNodes, edges: newEdges },
        { messagingGroups, messagingGroupsLoaded },
      )
      const shouldForceHydrate =
        hydrateToken !== undefined &&
        hydrateToken !== lastHydrateTokenRef.current
      const contentDataChanged =
        lastContentDataSignatureRef.current !== '' &&
        lastContentDataSignatureRef.current !== newContentDataSignature

      if (
        shouldForceHydrate ||
        contentDataChanged ||
        lastStructureRef.current === '' ||
        lastStructureRef.current !== newStructure
      ) {
        isSyncingRef.current = true
        lastStructureRef.current = newStructure
        lastContentDataSignatureRef.current = newContentDataSignature
        lastHydrateTokenRef.current = hydrateToken

        // Normalize, deduplicate, and clean edges
        const cleanedEdges = cleanEdges(newEdges)

        setNodes((currentNodes) => {
          if (shouldForceHydrate) {
            return newNodes
          }

          const nodeMap = new Map(currentNodes.map((n) => [n.id, n]))
          return newNodes.map((newNode) => {
            const existingNode = nodeMap.get(newNode.id)
            if (
              existingNode &&
              existingNode.type === newNode.type &&
              existingNode.position.x === newNode.position.x &&
              existingNode.position.y === newNode.position.y
            ) {
              if (contentDataChanged) {
                return {
                  ...existingNode,
                  data: newNode.data,
                }
              }
              return existingNode
            }
            return newNode
          })
        })
        setEdges(cleanedEdges)
        isSyncingRef.current = false
      }
    }
  }, [
    hydrateToken,
    initialData,
    messagingGroups,
    messagingGroupsLoaded,
    setNodes,
    setEdges,
  ])

  useEffect(() => {
    if (initialData) {
      const newNodes = (initialData.nodes ?? []) as SwitchboardNode[]
      // Create signature of validation errors to detect changes
      const validationErrorsSignature = JSON.stringify(
        newNodes
          .map((n) => ({
            id: n.id,
            validationErrors: (n.data as Record<string, unknown>)
              .validationErrors,
          }))
          .filter((n) => n.validationErrors)
          .sort((a, b) => a.id.localeCompare(b.id)),
      )

      // Only update if validation errors changed
      if (lastValidationErrorsRef.current !== validationErrorsSignature) {
        lastValidationErrorsRef.current = validationErrorsSignature

        // Update nodes with new validation errors
        setNodes((currentNodes) => {
          const nodeMap = new Map(
            newNodes.map((n) => [n.id, n.data as Record<string, unknown>]),
          )
          return currentNodes.map((node) => {
            const newData = nodeMap.get(node.id)
            if (
              newData &&
              (newData.validationErrors !== undefined ||
                (node.data as Record<string, unknown>).validationErrors !==
                  undefined)
            ) {
              // Merge validation errors, but preserve other node data
              return {
                ...node,
                data: {
                  ...node.data,
                  validationErrors: newData.validationErrors,
                },
              }
            }
            return node
          })
        })
      }
    }
  }, [initialData, setNodes])

  const showValidationError = useCallback(
    (errorMessage: string, _connection?: Connection) => {
      if (toast) {
        toast.create({
          title: text(
            t,
            'switchboard.edit.canvas.validationToastTitle',
            'Warning!',
          ),
          description: errorMessage,
          type: 'warning',
        })
      }
    },
    [toast, t],
  )

  const showAutoAttendantLoopWarningIfNeeded = useCallback(
    (
      sourceNode: SwitchboardNode | undefined,
      targetNode: SwitchboardNode | undefined,
      sourceHandle: string | null | undefined,
    ) => {
      if (!toast || !sourceNode || !targetNode) return
      if (sourceNode.type !== 'auto_attendant') return
      if (sourceNode.id !== targetNode.id) return
      if (!sourceHandle || !sourceHandle.startsWith('option-')) return

      toast.create({
        title: text(
          t,
          'switchboard.edit.canvas.autoAttendantLoop.title',
          'Auto Attendant Loop Enabled',
        ),
        description: text(
          t,
          'switchboard.edit.canvas.autoAttendantLoop.description',
          'This option loops callers back to the beginning of this auto attendant.',
        ),
        type: 'info',
      })
    },
    [toast, t],
  )

  const clearStaleClickConnectionState = useCallback(() => {
    const storeState = reactFlowStore.getState() as ReactFlowInternalStore
    const hasClickStartHandle = Boolean(storeState.connectionClickStartHandle)
    const hasInProgressConnection = Boolean(
      storeState.connection?.inProgress ?? storeState.connection?.fromHandle,
    )

    if (hasClickStartHandle) {
      reactFlowStore.setState({ connectionClickStartHandle: null } as never)
    }

    if (
      hasInProgressConnection &&
      !isDraggingConnectionStateRef.current &&
      typeof storeState.cancelConnection === 'function'
    ) {
      storeState.cancelConnection()
    }
  }, [reactFlowStore])

  const resetConnectionDragState = useCallback(() => {
    setIsDraggingConnection(false)
    edgeToReconnectRef.current = null
    isDraggingFromConnectedTargetRef.current = false
  }, [])

  // Brief red flash on duplicate-connection attempt
  const highlightExistingEdge = useCallback((edgeId: string) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
    }
    setHighlightedEdgeId(edgeId)
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedEdgeId(null)
      highlightTimeoutRef.current = null
    }, 2000)
  }, [])

  const reactFlowWrapper = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const wrapper = reactFlowWrapper.current
    if (!wrapper) return

    const onPointerDownCapture = (event: PointerEvent) => {
      const eventTarget = event.target
      if (
        event.button === 0 &&
        eventTarget instanceof Element &&
        eventTarget.closest('.react-flow__handle')
      ) {
        clearStaleClickConnectionState()
      }
    }

    wrapper.addEventListener('pointerdown', onPointerDownCapture, true)

    return () => {
      wrapper.removeEventListener('pointerdown', onPointerDownCapture, true)
    }
  }, [clearStaleClickConnectionState])

  // onConnectStart: detect reconnection vs new connection
  const onConnectStart: OnConnectStart = useCallback(
    (_event, { handleId, nodeId, handleType }) => {
      if (readOnly) return

      setIsDraggingConnection(true)
      isDraggingFromConnectedTargetRef.current = false

      if (nodeId) {
        if (handleType === 'source') {
          // Source handle - check if reconnecting an existing edge
          // Note: handleId can be null/undefined for default source handles (no explicit id prop)
          // We need to match edges where sourceHandle matches handleId (including null/undefined)
          const existingEdge = edges.find((e) => {
            if (e.source !== nodeId) return false
            // Normalize both to null for comparison
            const edgeHandle = e.sourceHandle ?? null
            const currentHandle = handleId ?? null
            return edgeHandle === currentHandle
          })

          if (existingEdge) {
            // We're reconnecting an existing edge from the source handle
            edgeToReconnectRef.current = existingEdge.id
          } else {
            edgeToReconnectRef.current = null
          }
        } else if (handleType === 'target') {
          // Target handle - check if it already has an incoming connection
          const existingEdge = edges.find(
            (e) =>
              e.target === nodeId &&
              (!handleId || !e.targetHandle || e.targetHandle === handleId),
          )

          if (existingEdge) {
            // Block connection line - this target already has a connection
            isDraggingFromConnectedTargetRef.current = true
          }

          edgeToReconnectRef.current = null
        }
      } else {
        edgeToReconnectRef.current = null
      }
    },
    [readOnly, edges],
  )

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return

      // Always validate connection before creating
      const sourceNode = nodesMapRef.current.get(params.source)
      const targetNode = nodesMapRef.current.get(params.target)

      // Check if we're reconnecting an existing edge (from source handle)
      const edgeToReconnectId = edgeToReconnectRef.current
      const isReconnecting = edgeToReconnectId !== null

      // When reconnecting, exclude the edge being reconnected from duplicate check
      const edgesForValidation = isReconnecting
        ? edges.filter((e) => e.id !== edgeToReconnectId)
        : edges

      const validation = validateConnection(
        sourceNode,
        targetNode,
        params.sourceHandle,
        undefined,
        edgesForValidation,
        t,
      )

      if (!validation.valid) {
        const errorMessage =
          validation.errorMessage ||
          text(
            t,
            'switchboard.edit.canvas.invalidConnection',
            'Invalid connection',
          )
        showValidationError(errorMessage, params)

        if (validation.existingEdge) {
          highlightExistingEdge(validation.existingEdge.id)
        }

        resetConnectionDragState()
        return
      }

      showAutoAttendantLoopWarningIfNeeded(
        sourceNode,
        targetNode,
        params.sourceHandle,
      )

      // If we're reconnecting an existing edge from source handle, update the target
      if (isReconnecting && edgeToReconnectId) {
        setEdges((eds) => {
          const edgeToReconnect = eds.find((e) => e.id === edgeToReconnectId)
          if (!edgeToReconnect) {
            edgeToReconnectRef.current = null
            return eds
          }

          const newEdgeId = createEdgeKey(
            edgeToReconnect.source,
            params.target,
            edgeToReconnect.sourceHandle || params.sourceHandle,
          )

          return eds.map((e) =>
            e.id === edgeToReconnectId
              ? {
                  ...e,
                  id: newEdgeId,
                  target: params.target,
                  targetHandle: params.targetHandle,
                }
              : e,
          )
        })
        resetConnectionDragState()
        return
      }

      // Otherwise, create a new edge
      const edgeId = createEdgeKey(
        params.source,
        params.target,
        params.sourceHandle,
      )

      setEdges((eds) => {
        let existingEdge = eds.find((e) => e.id === edgeId)

        if (!existingEdge && params.sourceHandle) {
          existingEdge = eds.find(
            (e) =>
              e.source === params.source &&
              e.target === params.target &&
              e.sourceHandle === params.sourceHandle,
          )
        }

        if (existingEdge) {
          if (
            params.sourceHandle &&
            !existingEdge.sourceHandle &&
            existingEdge
          ) {
            const existingEdgeId = existingEdge.id
            return eds.map((e) =>
              e.id === existingEdgeId
                ? { ...e, sourceHandle: params.sourceHandle }
                : e,
            )
          }

          return eds
        }

        const newEdge: SwitchboardEdge = {
          id: edgeId,
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle,
          targetHandle: params.targetHandle,
          type: 'default',
          label: undefined,
        }

        return [...eds, newEdge]
      })
      resetConnectionDragState()
    },
    [
      setEdges,
      readOnly,
      showValidationError,
      showAutoAttendantLoopWarningIfNeeded,
      edges,
      highlightExistingEdge,
      resetConnectionDragState,
      t,
    ],
  )

  const onConnectEnd: OnConnectEnd = useCallback(
    (_event, connectionState: FinalConnectionState) => {
      if (readOnly) return

      let connection: Connection | null = null

      if (connectionState && typeof connectionState === 'object') {
        if ('connection' in connectionState) {
          const conn = (connectionState as { connection?: Connection })
            .connection
          if (conn && 'source' in conn && 'target' in conn) {
            connection = conn
          }
        } else if (
          'source' in connectionState &&
          'target' in connectionState &&
          'sourceHandle' in connectionState
        ) {
          connection = connectionState as unknown as Connection
        }
      }

      resetConnectionDragState()

      // If we have a connection and it was invalid, validate and show toast
      if (connection && !connectionState.isValid) {
        const sourceNode = nodesMapRef.current.get(connection.source)
        const targetNode = nodesMapRef.current.get(connection.target)

        const validation = validateConnection(
          sourceNode,
          targetNode,
          connection.sourceHandle,
          undefined,
          edges,
          t,
        )

        if (!validation.valid && validation.errorMessage) {
          showValidationError(validation.errorMessage, connection)
          if (validation.existingEdge) {
            highlightExistingEdge(validation.existingEdge.id)
          }
        }
      }
    },
    [
      readOnly,
      showValidationError,
      edges,
      highlightExistingEdge,
      resetConnectionDragState,
      t,
    ],
  )

  // Handle native edge reconnection (when user drags from edge endpoint)
  // This is used for source-side reconnection where React Flow handles the visual
  const onReconnect: OnReconnect<SwitchboardEdge> = useCallback(
    (oldEdge, newConnection) => {
      if (readOnly) return

      // Validate the new connection
      const sourceNode = nodesMapRef.current.get(newConnection.source)
      const targetNode = nodesMapRef.current.get(newConnection.target)

      // Exclude the old edge from duplicate checking
      const edgesForValidation = edges.filter((e) => e.id !== oldEdge.id)
      const validation = validateConnection(
        sourceNode,
        targetNode,
        newConnection.sourceHandle,
        undefined,
        edgesForValidation,
        t,
      )

      if (!validation.valid) {
        showValidationError(
          validation.errorMessage ||
            text(
              t,
              'switchboard.edit.canvas.invalidConnection',
              'Invalid connection',
            ),
          newConnection,
        )
        if (validation.existingEdge) {
          highlightExistingEdge(validation.existingEdge.id)
        }
        return
      }

      showAutoAttendantLoopWarningIfNeeded(
        sourceNode,
        targetNode,
        newConnection.sourceHandle,
      )

      // Use React Flow's reconnectEdge utility to update the edge
      setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds))
    },
    [
      readOnly,
      edges,
      setEdges,
      showValidationError,
      highlightExistingEdge,
      showAutoAttendantLoopWarningIfNeeded,
      t,
    ],
  )

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (node) {
        // Use getNodeLabel to get the correct label (handles AI Agent label update)
        const nodeLabel = getNodeLabel(node.type as NodeType, t)
        setNodeToDelete({
          id: nodeId,
          label: nodeLabel,
        })
      }
    },
    [t],
  )

  const confirmDeleteNode = useCallback(() => {
    if (!nodeToDelete) return

    setNodes((nds) => nds.filter((n) => n.id !== nodeToDelete.id))
    setEdges((eds) =>
      eds.filter(
        (e) => e.source !== nodeToDelete.id && e.target !== nodeToDelete.id,
      ),
    )
    setNodeToDelete(null)
  }, [nodeToDelete, setNodes, setEdges])

  const cancelDeleteNode = useCallback(() => {
    setNodeToDelete(null)
  }, [])

  const onEdgeClick = useCallback(
    (_event: React.MouseEvent<Element, MouseEvent>, edge: SwitchboardEdge) => {
      if (readOnly) return
      setEdgeToDelete(edge.id)
    },
    [readOnly],
  )

  const confirmDeleteEdge = useCallback(() => {
    if (!edgeToDelete) return

    setEdges((eds) => eds.filter((e) => e.id !== edgeToDelete))
    setEdgeToDelete(null)
  }, [edgeToDelete, setEdges])

  const cancelDeleteEdge = useCallback(() => {
    setEdgeToDelete(null)
  }, [])

  const highlightedHandlesRef = useRef<Element[]>([])

  // Find handle by ID, or default handle, or any
  const findHandle = useCallback(
    (
      nodeElement: Element,
      handleType: 'source' | 'target',
      handleId?: string | null,
    ): Element | null => {
      const baseSelector = `.react-flow__handle.${handleType}`
      if (handleId) {
        const handle = nodeElement.querySelector(
          `${baseSelector}[data-handleid="${handleId}"]`,
        )
        if (handle) return handle
      }
      const defaultHandle = nodeElement.querySelector(
        `${baseSelector}:not([data-handleid])`,
      )
      if (defaultHandle) return defaultHandle
      return nodeElement.querySelector(baseSelector)
    },
    [],
  )

  const onEdgeMouseEnter = useCallback(
    (_event: React.MouseEvent, edge: SwitchboardEdge) => {
      setHoveredEdgeId(edge.id)

      const wrapper = reactFlowWrapper.current
      if (!wrapper) return

      const handlesToHighlight: Element[] = []
      const sourceNode = wrapper.querySelector(`[data-id="${edge.source}"]`)
      if (sourceNode) {
        const sourceHandle = findHandle(sourceNode, 'source', edge.sourceHandle)
        if (sourceHandle) {
          sourceHandle.classList.add('highlighted')
          handlesToHighlight.push(sourceHandle)
        }
      }
      const targetNode = wrapper.querySelector(`[data-id="${edge.target}"]`)
      if (targetNode) {
        const targetHandle = findHandle(targetNode, 'target', edge.targetHandle)
        if (targetHandle) {
          targetHandle.classList.add('highlighted')
          handlesToHighlight.push(targetHandle)
        }
      }

      highlightedHandlesRef.current = handlesToHighlight
    },
    [findHandle],
  )

  const onEdgeMouseLeave = useCallback(() => {
    setHoveredEdgeId(null)
    highlightedHandlesRef.current.forEach((handle) => {
      handle.classList.remove('highlighted')
    })
    highlightedHandlesRef.current = []
  }, [])

  useEffect(() => {
    if (pendingNodeType && !readOnly) {
      const handleAddNode = (event: MouseEvent) => {
        // Get the ReactFlow element to calculate position relative to canvas
        const reactFlowElement = document.querySelector('.react-flow')

        if (!reactFlowElement) {
          // Fallback: use click position if ReactFlow element not found
          const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          })

          const newNode: SwitchboardNode = {
            id: `${pendingNodeType}-${Date.now()}`,
            type: pendingNodeType,
            position,
            data: {
              label: getNodeLabel(pendingNodeType, t),
              ...getDefaultNodeData(pendingNodeType),
            },
          }

          setNodes((nds) => [...nds, newNode])
          onNodeAdded?.()
          return
        }

        // Get the ReactFlow viewport bounds
        const reactFlowBounds = reactFlowElement.getBoundingClientRect()

        // Calculate position in top right corner, just below utility bar
        // Header bar is typically ~150-180px tall (title + NodePalette + padding)
        // Place node at ~75% from left edge, ~200px from top (below header)
        const headerHeight = 220 // Approximate header + NodePalette height
        const rightMargin = reactFlowBounds.width * 0.1 // 10% from right edge
        const topMargin = headerHeight + 20 // Below header with small padding

        // Calculate screen coordinates relative to ReactFlow viewport
        const screenX =
          reactFlowBounds.left + reactFlowBounds.width - rightMargin
        const screenY = reactFlowBounds.top + topMargin

        // Convert screen coordinates to flow coordinates
        const position = screenToFlowPosition({
          x: screenX,
          y: screenY,
        })

        const newNode: SwitchboardNode = {
          id: `${pendingNodeType}-${Date.now()}`,
          type: pendingNodeType,
          position,
          data: {
            label: getNodeLabel(pendingNodeType, t),
            ...getDefaultNodeData(pendingNodeType), // Apply defaults
          },
        }

        setNodes((nds) => [...nds, newNode])
        onNodeAdded?.()
      }

      document.addEventListener('click', handleAddNode, { once: true })
      return () => {
        document.removeEventListener('click', handleAddNode)
      }
    }
  }, [pendingNodeType, readOnly, screenToFlowPosition, setNodes, onNodeAdded])

  type NodeHandleConfig = {
    isValidHandle: (handle: string | null | undefined) => boolean
    getDataProperty: (handle: string) => string
    getHandleTypes: (nodeData: Record<string, unknown>) => string[]
  }

  const nodeHandleConfigs: Record<string, NodeHandleConfig> = {
    auto_attendant: {
      isValidHandle: (handle) => isCustomHandleType(handle),
      getDataProperty: (handle) => handle, // onInvalid -> onInvalid, onTimeout -> onTimeout
      getHandleTypes: () => ['onInvalid', 'onTimeout'],
    },
    schedule: {
      isValidHandle: (handle) =>
        SCHEDULE_HANDLE_TYPES.includes(
          handle as 'whenOpen' | 'whenClosed' | 'holiday',
        ),
      getDataProperty: (handle) => handle, // whenOpen -> whenOpen, etc.
      getHandleTypes: () => ['whenOpen', 'whenClosed', 'holiday'],
    },
    day_night: {
      isValidHandle: (handle) =>
        DAY_NIGHT_HANDLE_TYPES.includes(handle as 'whenOpen' | 'whenClosed'),
      getDataProperty: (handle) => handle, // whenOpen -> whenOpen, etc.
      getHandleTypes: () => ['whenOpen', 'whenClosed'],
    },
    queue: {
      isValidHandle: (handle) =>
        QUEUE_HANDLE_TYPES.includes(handle as 'onTimeout' | 'onEscape'),
      getDataProperty: (handle) => handle, // onTimeout -> onTimeout, etc.
      getHandleTypes: () => ['onTimeout', 'onEscape'],
    },
    transfer_and_rollover: {
      isValidHandle: (handle) =>
        TRANSFER_AND_ROLLOVER_HANDLE_TYPES.includes(
          handle as 'noAnswer' | 'busy',
        ),
      getDataProperty: (handle) => handle, // noAnswer -> noAnswer, etc.
      getHandleTypes: () => ['noAnswer', 'busy'],
    },
    call_distribution: {
      isValidHandle: (handle) =>
        handle !== null && handle !== undefined && handle.startsWith('option-'),
      getDataProperty: (handle) => handle, // option-0 -> option-0, etc.
      getHandleTypes: (nodeData) =>
        Object.keys(nodeData).filter((key) => key.startsWith('option-')),
    },
  }

  const isSyncingCustomHandlesRef = useRef(false)
  const prevCustomHandlesRef = useRef<string>('')

  useEffect(() => {
    // Skip custom handles sync during drag for better performance
    if (
      isSyncingRef.current ||
      isSyncingCustomHandlesRef.current ||
      isDraggingRef.current
    )
      return

    // Use Map lookups instead of array.find() for O(1) performance
    // Use join() instead of JSON.stringify for better performance
    const customHandlesSignature = edges
      .filter((e) => {
        if (!e.sourceHandle) return false
        // Use O(1) Map lookup instead of O(n) array.find()
        const sourceNode = nodesMapRef.current.get(e.source)
        if (!sourceNode) return false
        const config = nodeHandleConfigs[sourceNode.type]
        return config?.isValidHandle(e.sourceHandle) ?? false
      })
      .map((e) => createEdgeKey(e.source, e.target, e.sourceHandle))
      .sort()
      .join('|') // Use join instead of JSON.stringify for better performance

    if (prevCustomHandlesRef.current === customHandlesSignature) {
      return
    }

    isSyncingCustomHandlesRef.current = true
    prevCustomHandlesRef.current = customHandlesSignature

    const nodesToUpdate = new Map<string, Partial<Record<string, unknown>>>()

    edges.forEach((edge) => {
      if (!edge.sourceHandle) return

      // Use O(1) Map lookup instead of O(n) array.find()
      const sourceNode = nodesMapRef.current.get(edge.source)
      if (!sourceNode) return

      const config = nodeHandleConfigs[sourceNode.type]
      if (!config?.isValidHandle(edge.sourceHandle)) return

      const update = nodesToUpdate.get(edge.source) || {}
      const dataProperty = config.getDataProperty(edge.sourceHandle)
      update[dataProperty] = edge.target
      nodesToUpdate.set(edge.source, update)
    })

    // Create edges map for faster lookups
    const edgesBySourceMap = new Map<string, Set<string>>()
    edges.forEach((edge) => {
      if (!edge.source || !edge.sourceHandle) return
      const sourceId = edge.source
      if (!edgesBySourceMap.has(sourceId)) {
        edgesBySourceMap.set(sourceId, new Set())
      }
      const edgeSet = edgesBySourceMap.get(sourceId)
      if (edgeSet) {
        edgeSet.add(edge.sourceHandle)
      }
    })

    nodes.forEach((node) => {
      const config = nodeHandleConfigs[node.type]
      if (!config) return

      const nodeData = node.data as Record<string, unknown>
      const update = nodesToUpdate.get(node.id) || {}
      const handleTypes = config.getHandleTypes(nodeData)

      // Use Map lookup instead of array.some() for better performance
      const nodeEdges = edgesBySourceMap.get(node.id) || new Set()
      handleTypes.forEach((handleType) => {
        const hasEdge = nodeEdges.has(handleType)
        if (!hasEdge && nodeData[handleType]) {
          update[handleType] = undefined
        }
      })

      if (Object.keys(update).length > 0) {
        nodesToUpdate.set(node.id, update)
      }
    })

    if (nodesToUpdate.size > 0) {
      // Use startTransition for non-urgent updates
      startTransition(() => {
        setNodes((nds) => {
          let hasChanges = false
          const updatedNodes = nds.map((node) => {
            const update = nodesToUpdate.get(node.id)
            if (update) {
              const nodeData = node.data as Record<string, unknown>
              const hasDataChange = Object.keys(update).some(
                (key) => update[key] !== nodeData[key],
              )

              if (hasDataChange) {
                hasChanges = true
                return {
                  ...node,
                  data: {
                    ...node.data,
                    ...update,
                  },
                }
              }
            }
            return node
          })

          return hasChanges ? updatedNodes : nds
        })
      })
    }

    isSyncingCustomHandlesRef.current = false
  }, [edges, nodes, setNodes])

  const onDataChangeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const flushPendingDataChange = useCallback(() => {
    if (
      !onDataChange ||
      isSyncingRef.current ||
      isSyncingCustomHandlesRef.current
    ) {
      return
    }

    if (onDataChangeTimeoutRef.current) {
      clearTimeout(onDataChangeTimeoutRef.current)
      onDataChangeTimeoutRef.current = null
    }

    onDataChange({
      nodes: nodesRef.current,
      edges: edgesRef.current,
    })
  }, [onDataChange])

  useEffect(() => {
    if (!registerFlushPendingDataChange) return

    registerFlushPendingDataChange(flushPendingDataChange)

    return () => {
      registerFlushPendingDataChange(() => {})
    }
  }, [flushPendingDataChange, registerFlushPendingDataChange])

  useEffect(() => {
    // Skip onDataChange entirely during drag - we'll call it after drag ends
    if (
      isDraggingRef.current ||
      !onDataChange ||
      isSyncingRef.current ||
      isSyncingCustomHandlesRef.current
    ) {
      return
    }

    // Clear any pending timeout
    if (onDataChangeTimeoutRef.current) {
      clearTimeout(onDataChangeTimeoutRef.current)
    }

    // Debounce to prevent rapid-fire calls
    onDataChangeTimeoutRef.current = setTimeout(() => {
      onDataChange({ nodes, edges })
      onDataChangeTimeoutRef.current = null
    }, ON_DATA_CHANGE_DEBOUNCE_MS)

    return () => {
      if (onDataChangeTimeoutRef.current) {
        clearTimeout(onDataChangeTimeoutRef.current)
        onDataChangeTimeoutRef.current = null
      }
    }
  }, [nodes, edges, onDataChange])

  // Handle drag start/stop to optimize performance
  const onNodeDragStart = useCallback(() => {
    isDraggingRef.current = true
    // Clear any pending timeouts to prevent updates during drag
    if (onDataChangeTimeoutRef.current) {
      clearTimeout(onDataChangeTimeoutRef.current)
      onDataChangeTimeoutRef.current = null
    }
  }, [])

  const onNodeDrag = useCallback(() => {
    isDraggingRef.current = true
  }, [])

  const onNodeDragStop = useCallback(() => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current)
    }
    dragTimeoutRef.current = setTimeout(() => {
      isDraggingRef.current = false
      dragTimeoutRef.current = null
      if (
        onDataChange &&
        !isSyncingRef.current &&
        !isSyncingCustomHandlesRef.current
      ) {
        startTransition(() => {
          onDataChange({ nodes, edges })
        })
      }
    }, 100)
  }, [nodes, edges, onDataChange])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current)
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [])

  const nodeTypesWithExtensionMap = useMemo(() => {
    return getNodeTypes(
      extensionMap,
      extensionsList,
      messagingGroups,
      soundClips,
      schedules,
      phoneNumbers,
      readOnly ? undefined : handleDeleteNode,
    )
  }, [
    extensionMap,
    extensionsList,
    messagingGroups,
    soundClips,
    schedules,
    phoneNumbers,
    readOnly,
    handleDeleteNode,
  ])

  const edgesMapRef = useRef<Map<string, boolean>>(new Map())

  useEffect(() => {
    const map = new Map<string, boolean>()
    edges.forEach((e) => {
      const key = createEdgeKey(
        e.source,
        e.target,
        e.sourceHandle,
        e.targetHandle,
      )
      map.set(key, true)
    })
    edgesMapRef.current = map
  }, [edges])

  const nodesMapRef = useRef<Map<string, SwitchboardNode>>(new Map())

  useEffect(() => {
    const map = new Map<string, SwitchboardNode>()
    nodes.forEach((n) => {
      map.set(n.id, n)
    })
    nodesMapRef.current = map
  }, [nodes])

  const isValidConnection: IsValidConnection<SwitchboardEdge> = useCallback(
    (edge: SwitchboardEdge | Connection) => {
      // Block connection line when dragging from a target handle that already has a connection
      if (isDraggingFromConnectedTargetRef.current) {
        return false
      }

      const edgeToReconnectId = edgeToReconnectRef.current
      if (edgeToReconnectId) {
        return true
      }

      const sourceHandle = edge.sourceHandle ?? null
      const targetHandle = edge.targetHandle ?? null
      const edgeKey = createEdgeKey(
        edge.source,
        edge.target,
        sourceHandle,
        targetHandle,
      )

      if (edgesMapRef.current.has(edgeKey)) {
        return false
      }

      return true
    },
    [],
  )

  const { token } = useChakraContext()
  const backgroundDotColor = token.var('colors.fg.default')
  const canvasBg = token.var('colors.bg.emphasized')
  const redSolid = token.var('colors.red.solid')
  const edgeColor = token.var(`colors.${EDGE_STROKE_COLOR_TOKEN}`)
  const edgeColorHover = token.var(`colors.${EDGE_STROKE_COLOR_HOVER_TOKEN}`)

  const dayNightContextValue = useMemo<DayNightSettingsContextValue>(
    () => ({
      api,
      settings: dayNightSettings ?? null,
      pbxId: pbxId ?? null,
      toast,
    }),
    [api, dayNightSettings, pbxId, toast],
  )

  const styledEdges = useMemo(() => {
    const edgeToReconnectId = edgeToReconnectRef.current

    const processedEdges = edges.map((edge) => {
      if (isDraggingConnection && edge.id === edgeToReconnectId) {
        return {
          ...edge,
          style: {
            ...edge.style,
            strokeOpacity: 0,
            strokeWidth: 0,
          },
          hidden: true,
          selectable: false,
          focusable: false,
          reconnectable: true,
        }
      }

      // Default: brand.focusRing with 2px stroke
      let stroke = edgeColor
      let strokeWidth = EDGE_STROKE_WIDTH

      // Error highlight: red with thicker stroke
      if (edge.id === highlightedEdgeId) {
        stroke = redSolid
        strokeWidth = EDGE_STROKE_WIDTH_HIGHLIGHTED
      }

      // Hover: brand.fg with 4px stroke
      const isHovered = edge.id === hoveredEdgeId
      if (isHovered) {
        stroke = edgeColorHover
        strokeWidth = EDGE_STROKE_WIDTH_HIGHLIGHTED
      }

      return {
        ...edge,
        style: {
          stroke,
          strokeWidth,
          strokeOpacity: 1,
        },
        interactionWidth: EDGE_INTERACTION_WIDTH,
        animated: edge.id === highlightedEdgeId,
        reconnectable: true,
        className: isHovered ? 'hovered' : undefined,
      }
    })

    return processedEdges
  }, [
    edges,
    highlightedEdgeId,
    hoveredEdgeId,
    isDraggingConnection,
    redSolid,
    edgeColor,
    edgeColorHover,
  ])

  return (
    <SwitchboardI18nContext.Provider value={t}>
      <DayNightSettingsContext.Provider value={dayNightContextValue}>
        <Box
          ref={reactFlowWrapper}
          width="100%"
          height="100%"
          position="relative"
        >
          <ReactFlow
            nodes={nodes}
            edges={styledEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnectStart={readOnly ? undefined : onConnectStart}
            onConnect={readOnly ? undefined : onConnect}
            onConnectEnd={readOnly ? undefined : onConnectEnd}
            onReconnect={readOnly ? undefined : onReconnect}
            onEdgeClick={readOnly ? undefined : onEdgeClick}
            onEdgeMouseEnter={onEdgeMouseEnter}
            onEdgeMouseLeave={onEdgeMouseLeave}
            onNodeDragStart={readOnly ? undefined : onNodeDragStart}
            onNodeDrag={readOnly ? undefined : onNodeDrag}
            onNodeDragStop={readOnly ? undefined : onNodeDragStop}
            isValidConnection={readOnly ? undefined : isValidConnection}
            connectOnClick={false}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
            nodeTypes={nodeTypesWithExtensionMap}
            deleteKeyCode={null}
            noDragClassName="nodrag"
            noWheelClassName="nowheel"
            noPanClassName="nopan"
            colorMode={colorMode}
            defaultEdgeOptions={{
              style: {
                stroke: edgeColor,
                strokeWidth: EDGE_STROKE_WIDTH,
                strokeOpacity: 1,
              },
            }}
            connectionLineStyle={{
              stroke: edgeColor,
              strokeWidth: EDGE_STROKE_WIDTH,
            }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            style={{ width: '100%', height: '100%' }}
            onlyRenderVisibleElements={true}
            elevateNodesOnSelect={false}
            nodesFocusable={false}
            edgesFocusable={false}
            selectNodesOnDrag={false}
            nodeOrigin={[0.5, 0.5]}
            proOptions={{ hideAttribution: true }}
          >
            <Background
              color={backgroundDotColor}
              gap={20}
              size={1}
              style={{ backgroundColor: canvasBg }}
            />
            <Controls />
          </ReactFlow>
          <DeleteNodeConfirmationDialog
            isOpen={!!nodeToDelete}
            nodeLabel={nodeToDelete?.label}
            onConfirm={confirmDeleteNode}
            onCancel={cancelDeleteNode}
            t={t}
          />
          <DeleteEdgeConfirmationDialog
            isOpen={!!edgeToDelete}
            onConfirm={confirmDeleteEdge}
            onCancel={cancelDeleteEdge}
            t={t}
          />
        </Box>
      </DayNightSettingsContext.Provider>
    </SwitchboardI18nContext.Provider>
  )
}

export const SwitchboardCanvas: React.FC<SwitchboardCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  )
}
